import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const Signup = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const BACKEND_URL = getBackendUrl();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    music_format: 'greek_mainstream',
    station_name: '',
    station_frequency: '',
    station_region: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  
  const musicFormats = [
    { id: 'chr', name: 'CHR (Current Hit Radio)' },
    { id: 'hot_ac', name: 'Hot AC' },
    { id: 'ac', name: 'AC (Adult Contemporary)' },
    { id: 'rock', name: 'Rock' },
    { id: 'gold_retro', name: 'Gold / Retro' },
    { id: 'greek_mainstream', name: 'Greek Mainstream' }
  ];
  
  const greekRegions = [
    'Αττική', 'Θεσσαλονίκη', 'Αχαΐα', 'Ηράκλειο', 'Λάρισα', 'Μαγνησία',
    'Πιερία', 'Κέρκυρα', 'Ιωάννινα', 'Χανιά', 'Ρέθυμνο', 'Λασίθι',
    'Ροδόπη', 'Έβρος', 'Δράμα', 'Καβάλα', 'Ξάνθη', 'Σέρρες', 'Κιλκίς',
    'Χαλκιδική', 'Ημαθία', 'Πέλλα', 'Φλώρινα', 'Κοζάνη', 'Καστοριά',
    'Γρεβενά', 'Τρίκαλα', 'Καρδίτσα', 'Άρτα', 'Πρέβεζα', 'Θεσπρωτία',
    'Λευκάδα', 'Κεφαλληνία', 'Ζάκυνθος', 'Αιτωλοακαρνανία', 'Φωκίδα',
    'Φθιώτιδα', 'Ευρυτανία', 'Βοιωτία', 'Εύβοια', 'Αργολίδα', 'Κορινθία',
    'Αρκαδία', 'Λακωνία', 'Μεσσηνία', 'Ηλεία', 'Κυκλάδες', 'Δωδεκάνησα',
    'Λέσβος', 'Χίος', 'Σάμος', 'Άλλος'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignup = async () => {
    setError('');
    
    if (!formData.username || !formData.email || !formData.password || 
        !formData.station_name || !formData.station_frequency || !formData.station_region) {
      setError('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    
    if (!agreed) {
      setError('Πρέπει να αποδεχτείτε τους όρους χρήσης');
      return;
    }
    
    setLoading(true);
    
    try {
      const signupResponse = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        music_format: formData.music_format,
        station_name: formData.station_name,
        station_frequency: formData.station_frequency,
        station_region: formData.station_region
      });
      
      const userId = signupResponse.data.user_id;
      const token = signupResponse.data.token;
      
      // Check if there's a pending package from pricing page
      const pendingPackage = sessionStorage.getItem('pendingPackage');
      const pendingBillingCycle = sessionStorage.getItem('pendingBillingCycle') || 'monthly';
      
      if (pendingPackage) {
        // Clear the pending package
        sessionStorage.removeItem('pendingPackage');
        sessionStorage.removeItem('pendingBillingCycle');
        
        // Create checkout for the selected package
        const checkoutResponse = await axios.post(
          `${BACKEND_URL}/api/checkout/create`,
          {
            package_id: pendingPackage,
            billing_cycle: pendingBillingCycle,
            origin_url: window.location.origin,
            user_id: userId,
            email: formData.email
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          // Fallback - go to pricing page
          navigate('/pricing');
        }
      } else if (signupResponse.data.checkout_url) {
        window.location.href = signupResponse.data.checkout_url;
      } else {
        // No pending package and no checkout URL - just go to home
        navigate('/');
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα εγγραφής. Παρακαλώ δοκιμάστε ξανά.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🎙️ Radio Content Generator
          </h1>
          <p className="text-gray-600">{t('createYourAccount') || 'Create your account'}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('username')}
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder={t('chooseUsername')}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📧 {t('email')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder="email@example.com"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📻 {t('musicFormat') || 'Music Format'}
            </label>
            <select
              name="music_format"
              value={formData.music_format}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              disabled={loading}
            >
              {musicFormats.map(format => (
                <option key={format.id} value={format.id}>
                  {format.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📡 {t('stationName') || 'Station Name'} *
            </label>
            <input
              type="text"
              name="station_name"
              value={formData.station_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder={t('stationNamePlaceholder') || 'e.g. Radio Athens'}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              📶 {t('frequency') || 'Frequency'} *
            </label>
            <input
              type="text"
              name="station_frequency"
              value={formData.station_frequency}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder={t('frequencyPlaceholder') || 'e.g. 95.5 FM'}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              🏛️ {t('region') || 'Region'} *
            </label>
            <select
              name="station_region"
              value={formData.station_region}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              disabled={loading}
            >
              <option value="">-- {t('selectRegion') || 'Select Region'} --</option>
              {greekRegions.map(region => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('password')}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('confirmPassword')}
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              style={{ backgroundColor: 'white', color: '#1f2937' }}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-2 border-green-300">
            <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
              <span className="text-xl">🎁</span> {t('trialPeriod24h') || 'Trial Period 24 Hours'}
            </h3>
            <div className="text-sm text-green-800 space-y-2">
              <p className="font-semibold">✅ {t('freeAccessFor24h') || 'Free access for 24 hours after registration!'}</p>
              <p>• {t('fullAccessAllFeatures') || 'Full access to all features'}</p>
              <p>• {t('noCreditCardForTrial') || 'No credit card required for trial'}</p>
              <p>• <strong>{t('noAutoCharge') || 'No automatic charge'}</strong></p>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">📦 {t('afterTrialExpires') || 'After trial expires'}</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p>{t('afterTrialInfo') || 'After 24 hours, choose a subscription package to continue access.'}</p>
              <p className="font-medium">🔒 {t('noAutoChargeYouChoose') || 'No automatic charge - You choose!'}</p>
              <a 
                href="/pricing" 
                className="inline-block mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-center"
              >
                📦 {t('viewPackages') || 'View Packages'}
              </a>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="agree" className="text-sm text-gray-600">
              {t('acceptTerms') || 'I accept the terms of use'}
            </label>
          </div>

          <button
            onClick={handleSignup}
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? `⏳ ${t('creatingAccount')}` : `🚀 ${t('createAccount') || 'Create Account'}`}
          </button>

          <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
            <p className="text-center text-sm text-gray-600">
              {t('alreadyHaveAccountQuestion') || 'Already have an account?'}{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                {t('loginHere') || 'Login here'}
              </button>
            </p>
            
            <p className="text-center text-sm text-gray-600">
              {t('justWantToTry') || 'Θέλετε μόνο να δοκιμάσετε;'}{' '}
              <button
                onClick={() => navigate('/trial-signup')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                🎁 {t('freeTrial24h') || '24ωρη Δωρεάν Δοκιμή'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;